import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { PageSideNav } from 'components/page';

interface Props {
  categories?: {
    id: number;
    title: string;
  }[];
}

const PathsNav = ({ categories }: Props) => {
  const { messages, serverConfig, user } = useReduxState();

  const router = useRouter();

  const activeType = router.query?.type as string;
  const categoryId = router.query?.categoryId as string | string[];
  const presentInAll = router.query?.presentInAll as string | string[];

  let preservedFilters = '';

  if (typeof categoryId === 'string') {
    preservedFilters += `&categoryId=${categoryId}`;
  } else if (typeof categoryId === 'object') {
    preservedFilters += categoryId.map((id) => `&categoryId=${id}`).join('');
  }

  if (typeof presentInAll === 'string') {
    preservedFilters += `&presentInAll=${presentInAll}`;
  } else if (typeof presentInAll === 'object') {
    preservedFilters += presentInAll
      .map((value) => `&presentInAll=${value}`)
      .join('');
  }

  const categoryFilters =
    categories?.length && !serverConfig.useAlternativeCategoryFilter
      ? {
          [messages.commonCategories]: [
            ...categories.map((category) => ({
              label: category.title,
              value: `&categoryId=${category.id}`
            }))
          ]
        }
      : {};

  const filters = {
    ...(['active', 'search'].includes(activeType)
      ? {
          [messages.commonStatus]: [
            { label: messages.commonCompleted, value: `&completionStatus=yes` },
            { label: messages.commonInProgress, value: `&completionStatus=no` }
          ]
        }
      : {}),
    ...categoryFilters
  };

  const filtersSeparate = {
    ...(categories?.length &&
      serverConfig.useAlternativeCategoryFilter && {
        [messages.commonCategories]: [
          ...categories.map((category) => ({
            label: category.title,
            value: `&categoryId=${category.id}`
          }))
        ]
      })
  };

  const sections = [
    {
      links: [
        {
          href: '/paths?type=all' + preservedFilters,
          icon: 'list' as const,
          isActive: activeType === 'all',
          text: messages.pathsAllPaths
        },
        ...(user?.roles.includes('admin')
          ? [
              {
                href: '/paths?type=administered' + preservedFilters,
                icon: 'edit' as const,
                isActive: activeType === 'administered',
                text: messages.coursesAdministered
              }
            ]
          : []),
        {
          href: '/paths?type=active' + preservedFilters,
          icon: 'tasks' as const,
          isActive: activeType === 'active',
          text: messages.pathsMyPaths
        },
        {
          href: '/paths?type=available' + preservedFilters,
          icon: 'book-open' as const,
          isActive: activeType === 'available',
          text: messages.commonAvailable
        }
      ]
    }
  ];

  return (
    <div className="nav">
      <PageSideNav
        filters={filters}
        filtersSeparate={filtersSeparate}
        filtersWithRequireAll={['categoryId']}
        linkSections={sections}
        shallowNavigation={true}
      />

      <style jsx>{`
        .nav {
          display: flex;
        }
      `}</style>
    </div>
  );
};

export default PathsNav;
